import { designVariables } from "@studio3t/design-variables";
import type { ReactNode } from "react";
import styled from "styled-components";

const SEMANTIC = designVariables.ui.semantic;
const CARDS = designVariables.ui.cards;
const HEADING = designVariables.type.heading;
const ROW_GAP = designVariables.primitives.spacings.s; // 4

// Vertical rhythm between stacked sections/cards on a detail page.
const Section = styled.section`
  margin-bottom: 20px;
`;

// Shared card pattern for the single-pipeline and single-service pages: a
// bordered card with an in-card 50px header (title + optional right-aligned
// actions), per Figma node 13495-42462 / 13022-8838. The body is supplied by
// the caller — wrap text/list content in CardBody for the standard padding, or
// render flush content (tables, the metrics grid) directly.
const CardShell = styled.div`
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: ${CARDS.corner}px;
  overflow: hidden;
  /* Fill the grid cell so side-by-side cards share the tallest card's height
     instead of sizing to content. */
  height: 100%;
`;

// Generic card header per Figma node 13495-42672 / 13022-8838 ("Card/header"): a
// 50px row, horizontal padding spacingm, with a left title group and a right
// actions group, space-between. `$divided` draws the separator line under the
// header (Figma 14064-11438 — the script cards' header/content divider).
const CardHeaderRow = styled.div<{ $divided?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SEMANTIC.spacings}px;
  min-height: 50px;
  padding: 0 ${SEMANTIC.spacingm}px;
  border-bottom: ${(props) =>
    props.$divided ? `1px solid ${props.theme.cards1Border}` : "none"};
`;

// Left slot: the title plus any title-adjacent content (e.g. a count badge),
// grouped with the standard spacing.
const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  min-width: 0;
`;

// Right slot: card actions, offset from the title by spacingl and grouped with
// the standard spacing. Sizes to its content.
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  padding-left: ${SEMANTIC.spacingl}px;
  flex-shrink: 0;
`;

// An <h2> so each card stays in the document heading outline for screen-reader
// navigation; default margins reset for the flex header row.
const CardTitle = styled.h2`
  margin: 0;
  font-size: ${HEADING.h6.fontSize}px;
  font-weight: ${HEADING.h6.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  color: ${(props) => props.theme.semanticTextHeading};
`;

export const CardBody = styled.div`
  padding: 0 ${SEMANTIC.spacingm}px ${SEMANTIC.spacingm}px;
`;

// --- Configuration card body (Figma node 13495-42518 / 13022-9397) -----------
// Stacked label/value rows: an uppercase light label sits inline with a semibold
// body value (gap semantic.spacings); rows are separated by spacings/s.
export const ConfigBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ROW_GAP}px;
  padding: 0 ${SEMANTIC.spacingm}px ${SEMANTIC.spacings}px;
`;

export const ConfigItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SEMANTIC.spacings}px;
  width: 100%;
`;

export const ConfigKey = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(props) => props.theme.semanticTextLight};
`;

export const ConfigVal = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(props) => props.theme.semanticTextBody};
`;

export const ConfigBadges = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: ${ROW_GAP}px;
  min-width: 0;
`;

export const ConfigActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: ${SEMANTIC.spacingm}px;
`;

export function SectionCard({
  title,
  actions,
  divided,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  /** Separator line between the header and the card content (Figma
   *  14064-11438, e.g. the script cards). */
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <Section>
      <CardShell>
        <CardHeaderRow $divided={divided}>
          <HeaderTitleGroup>
            <CardTitle>{title}</CardTitle>
          </HeaderTitleGroup>
          {actions ? <HeaderActions>{actions}</HeaderActions> : null}
        </CardHeaderRow>
        {children}
      </CardShell>
    </Section>
  );
}
